import { useState, useContext, useEffect } from 'react';
import { withFlagService } from '@atlassian/jira-flags';
import type {
	Actor,
	OnActorRemovedSuccess,
	AccessPageContext,
	RoleId,
	RoleTypeId,
} from '../../common/types';
import MainProviderContext from '../../services/access-page-context';
import TableResetterContext from '../table-filter-meta-controller';
import { MAX_RESULTS_PER_PAGE, RESULTS_PER_BATCH } from './constants';
import messages from './messages';
import type { Props, OnSortCallback, PageParameters } from './types';
import {
	isPageInNextBucket,
	isPageInPreviousBucket,
	getPageNumberForBatchStart,
	getRemoveActorSuccessMsgKey,
	getStartIndex,
	getNumberOfPages,
} from './utils';

const TableController = (props: Props) => {
	const {
		actors = [],
		onTableChange,
		children,
		tableFilters: {
			orderField = 'NAME',
			orderDirection = 'ASC',
			pageNumber = 1,
			pageSize = RESULTS_PER_BATCH,
		},
		flagService: { showFlag },
		isLastPage = true,
		isFetching,
		onDataChange,
	} = props;
	const numberOfVisiblePages = pageSize / MAX_RESULTS_PER_PAGE;
	const context: AccessPageContext = useContext(MainProviderContext);
	const [isRemoveActorDialogOpen, setIsRemoveActorDialogOpen] = useState<boolean>(false);
	const [currentActorToRemove, setCurrentActorToRemove] = useState<unknown>();

	const { isPageReset, togglePageReset, isFiltersApplied } = useContext(TableResetterContext);

	const initCurrentPage: number = getPageNumberForBatchStart(pageNumber, numberOfVisiblePages);

	const [pageParameters, setPageParameters] = useState<PageParameters>({
		currentPage: initCurrentPage,
		startIndex: getStartIndex(initCurrentPage, pageNumber, pageSize),
		numberOfPages: getNumberOfPages(pageNumber, actors.length, isFetching),
	});

	useEffect(() => {
		if (isPageReset) {
			const newCurrentPage: number = getPageNumberForBatchStart(pageNumber, numberOfVisiblePages);
			const oldStartIndex = pageParameters.startIndex;
			const oldNumberOfPages = pageParameters.numberOfPages;
			setPageParameters({
				startIndex: oldStartIndex,
				numberOfPages: oldNumberOfPages,
				currentPage: newCurrentPage,
			});
			togglePageReset(false);
		} else {
			const oldCurrentPage: number = pageParameters.currentPage;
			if (!isFetching) {
				setPageParameters({
					startIndex: getStartIndex(oldCurrentPage, pageNumber, pageSize),
					numberOfPages: getNumberOfPages(pageNumber, actors.length, isFetching),
					currentPage: oldCurrentPage,
				});
			}
		}
	}, [
		isFetching,
		pageNumber,
		pageSize,
		actors.length,
		isPageReset,
		numberOfVisiblePages,
		togglePageReset,
		pageParameters.currentPage,
		pageParameters.startIndex,
		pageParameters.numberOfPages,
	]);

	const onPageChange = (newPage: number): void => {
		if (isPageInNextBucket(newPage, pageNumber, numberOfVisiblePages)) {
			onTableChange('pageNumber', pageNumber + 1);
		}
		if (isPageInPreviousBucket(newPage, pageNumber, numberOfVisiblePages)) {
			onTableChange('pageNumber', pageNumber - 1);
		}
		setPageParameters({ ...pageParameters, currentPage: newPage });
	};

	const onRemoveActorDialogDismiss = (): void => setIsRemoveActorDialogOpen(false);
	const onActorRemovedSuccess: OnActorRemovedSuccess = (actor) => {
		onDataChange && onDataChange('REMOVE_ACTOR', actor.roleTypeId);
		onRemoveActorDialogDismiss();
		showFlag({
			type: 'success',
			title: messages.genericSuccessTitle,
			description: [
				messages[getRemoveActorSuccessMsgKey(actor, context)],
				{
					name: actor.displayName,
				},
			],
			testId: 'users-and-roles.controllers.table-controller.remove-actor.success',
		});
	};
	const onActorRemovedFailure = (isLastAdminError: undefined) => {
		onRemoveActorDialogDismiss();
		showFlag({
			type: 'error',
			title: messages.genericErrorTitle,
			description: isLastAdminError ? messages.lastAdminErrorMessage : messages.genericErrorMsg,
			testId: 'users-and-roles.controllers.table-controller.remove-actor.error',
		});
	};

	const onSelectActorToRemove = (actor: Actor): void => {
		setCurrentActorToRemove(actor);
		setIsRemoveActorDialogOpen(true);
	};

	const onRolesUpdated = (roles: RoleId[], roleTypeId: RoleTypeId): void => {
		onDataChange && onDataChange('UPDATE_ROLE', roleTypeId, roles);
	};

	const { startIndex, numberOfPages } = pageParameters;

	const filteredActors: Actor[] =
		startIndex >= actors.length
			? actors.slice(0, MAX_RESULTS_PER_PAGE)
			: actors.slice(startIndex, startIndex + MAX_RESULTS_PER_PAGE);

	const onSort: OnSortCallback = ({ key, order }) =>
		key !== orderField ? onTableChange('orderField', key) : onTableChange('orderDirection', order);

	return children({
		actors: filteredActors,
		pagination: {
			currentPage: pageParameters.currentPage,
			onPageChange,
			isLastPage,
			numberOfPages,
			pageNumber,
		},
		sorting: { onSort, key: orderField, order: orderDirection },
		isFiltersApplied,
		removeActor: {
			isOpen: isRemoveActorDialogOpen,
			onSelectActorToRemove,
			// @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'Actor | undefined'.
			currentActor: currentActorToRemove,
			onActorRemovedSuccess,
			// @ts-expect-error - TS2322 - Type '(isLastAdminError: undefined) => void' is not assignable to type '() => void'.
			onActorRemovedFailure,
			onDismiss: onRemoveActorDialogDismiss,
		},
		onRolesUpdated,
	});
};

export default withFlagService(TableController);
